import React, { useEffect, useState } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { getAllProfiles, updateProfile, supabase } from '../lib/supabase'

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*]).{8,}$/

export default function AdminUsers() {
  const { profile: myProfile, user } = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('users')
  const [inviteModal, setInviteModal] = useState(false)
  const [manualModal, setManualModal] = useState(false)
  const [pwResetRequests, setPwResetRequests] = useState([])
  const [msg, setMsg] = useState(null)
  const [inviting, setInviting] = useState(false)
  const [saving, setSaving] = useState(false)

  const [inviteForm, setInviteForm] = useState({ email: '', full_name: '', permission_level: 'staff', user_role: 'staff' })
  const [manualForm, setManualForm] = useState({ full_name: '', role: '', department: '', permission_level: 'staff', notes: '' })
  const [pwForm, setPwForm] = useState({ password: '', confirm: '' })
  const [pwResetting, setPwResetting] = useState(null) // user id being reset

  const [manualWorkers, setManualWorkers] = useState([])
  const [userTab, setUserTab] = useState('digital')

  useEffect(() => { loadUsers(); loadPwResets(); loadManualWorkers() }, [])

  const loadUsers = async () => {
    setLoading(true)
    const { data } = await getAllProfiles()
    setUsers(data || [])
    setLoading(false)
  }

  const loadManualWorkers = async () => {
    const { data } = await supabase.from('manual_workers').select('*').order('full_name')
    setManualWorkers(data || [])
  }

  const loadPwResets = async () => {
    const { data } = await supabase.from('password_reset_requests')
      .select('*, profiles(full_name, email)').eq('status', 'Pending').order('created_at', { ascending: false })
    setPwResetRequests(data || [])
  }

  const handleRoleChange = async (userId, newLevel) => {
    await updateProfile(userId, { permission_level: newLevel })
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, permission_level: newLevel } : u))
    setMsg({ type: 'success', text: 'Permission level updated.' })
    setTimeout(() => setMsg(null), 3000)
  }

  const handleDeactivate = async (userId, isActive) => {
    const action = isActive ? 'deactivate' : 'reactivate'
    // Use msg as confirmation instead of window.confirm
    setMsg({ type:'confirm', text:`Are you sure you want to ${action} this user?`, userId, isActive })
  }

  const handleDeactivateConfirm = async (userId, isActive) => {
    const action = isActive ? 'deactivate' : 'reactivate'
    await updateProfile(userId, { is_active: !isActive })
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: !isActive } : u))
    setMsg({ type: 'success', text: `User ${action}d.` })
    setTimeout(() => setMsg(null), 3000)
  }

  // Invite user with email — routes through /api/invite-user which has service role key
  const handleInvite = async () => {
    if (!inviteForm.email || !inviteForm.full_name) { setMsg({ type: 'error', text: 'Email and name are required.' }); return }
    setInviting(true)
    try {
      const res = await fetch('/api/invite-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: inviteForm.email,
          full_name: inviteForm.full_name,
          permission_level: inviteForm.permission_level,
          user_role: inviteForm.user_role,
        }),
      })
      const data = await res.json()
      if (!res.ok) {
        setMsg({ type: 'error', text: data.error || 'Failed to send invite.' })
        setInviting(false)
        return
      }
      setMsg({ type: 'success', text: data.message })
      setInviting(false)
      setInviteModal(false)
      setInviteForm({ email: '', full_name: '', permission_level: 'staff', user_role: 'staff' })
      loadUsers()
      setTimeout(() => setMsg(null), 6000)
    } catch (err) {
      setMsg({ type: 'error', text: 'Network error. Please check your connection and try again.' })
      setInviting(false)
    }
  }

  // Add manual worker (no email needed)
  const handleAddManual = async () => {
    if (!manualForm.full_name) { setMsg({ type: 'error', text: 'Full name is required.' }); return }
    setSaving(true)
    const { error } = await supabase.from('manual_workers').insert({
      full_name: manualForm.full_name,
      role: manualForm.role,
      department: manualForm.department,
      permission_level: manualForm.permission_level,
      notes: manualForm.notes,
      is_active: true,
      added_by: user.id,
    })
    if (error) { setMsg({ type: 'error', text: error.message }); setSaving(false); return }
    setMsg({ type: 'success', text: `${manualForm.full_name} added as a manual worker. Their timesheets and logs must be entered by a supervisor on their behalf.` })
    setSaving(false); setManualModal(false)
    setManualForm({ full_name: '', role: '', department: '', permission_level: 'staff', notes: '' })
    loadManualWorkers()
    setTimeout(() => setMsg(null), 6000)
  }

  // Approve password reset — send reset link via Supabase
  const handleApproveReset = async (request) => {
    setSaving(true)
    // Send password reset email via Supabase
    const { error } = await supabase.auth.admin.generateLink({
      type: 'recovery',
      email: request.profiles?.email,
    })
    if (error) { setMsg({ type: 'error', text: 'Failed to send reset link: ' + error.message }); setSaving(false); return }
    // Mark request as approved
    await supabase.from('password_reset_requests').update({
      status: 'Approved',
      approved_by: user.id,
      approved_at: new Date().toISOString(),
    }).eq('id', request.id)
    setMsg({ type: 'success', text: `Password reset link sent to ${request.profiles?.email}.` })
    setSaving(false); loadPwResets()
    setTimeout(() => setMsg(null), 4000)
  }

  const handleDenyReset = async (request) => {
    await supabase.from('password_reset_requests').update({ status: 'Denied', approved_by: user.id }).eq('id', request.id)
    loadPwResets()
  }

  const permColors = { admin: 'badge-red', manager: 'badge-green', staff: 'badge-amber', view_only: 'badge-grey' }
  const permLevels = ['admin', 'manager', 'staff', 'view_only']

  return (
    <AppLayout title="Manage Users">
      <div className="section-header">
        <div className="section-icon">👥</div>
        <div className="section-info">
          <h2>User Management</h2>
          <p>Manage staff accounts, permissions, and access.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button className="btn btn-secondary" onClick={() => setManualModal(true)}>+ Manual Worker</button>
          <button className="btn btn-primary" onClick={() => setInviteModal(true)}>+ Invite User</button>
        </div>
      </div>

      {msg && msg.type !== 'confirm' && <div className={`alert ${msg.type === 'success' ? 'alert-green' : 'alert-red'}`}>{msg.text}</div>}
      {msg && msg.type === 'confirm' && (
        <div className="alert alert-amber" style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span>⚠️ {msg.text}</span>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-ghost btn-sm" onClick={()=>setMsg(null)}>Cancel</button>
            <button className="btn btn-danger btn-sm" onClick={()=>handleDeactivateConfirm(msg.userId, msg.isActive)}>Confirm</button>
          </div>
        </div>
      )}

      {pwResetRequests.length > 0 && (
        <div className="alert alert-amber" style={{ marginBottom: 16 }}>
          🔑 <strong>{pwResetRequests.length} password reset request{pwResetRequests.length > 1 ? 's' : ''} pending</strong> — click the Password Resets tab to review.
        </div>
      )}

      <div className="tabs">
        <button className={`tab ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}>👤 Staff Accounts</button>
        <button className={`tab ${tab === 'pw' ? 'active' : ''}`} onClick={() => setTab('pw')}>
          🔑 Password Resets {pwResetRequests.length > 0 && <span className="badge badge-red" style={{ marginLeft: 6 }}>{pwResetRequests.length}</span>}
        </button>
      </div>

      {/* USERS TAB */}
      {tab === 'users' && (
        <div className="card">
          {/* Sub-tabs for digital vs manual */}
          <div style={{ display:'flex', gap:8, marginBottom:16 }}>
            <button onClick={()=>setUserTab('digital')} className={`btn ${userTab==='digital'?'btn-primary':'btn-secondary'} btn-sm`}>
              💻 Digital Staff ({users.length})
            </button>
            <button onClick={()=>setUserTab('manual')} className={`btn ${userTab==='manual'?'btn-primary':'btn-secondary'} btn-sm`}>
              📋 Manual Workers ({manualWorkers.length})
            </button>
          </div>

          {userTab === 'digital' && (
            loading ? <div style={{ textAlign: 'center', padding: 32 }}><span className="spinner dark" /></div>
            : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Name</th><th>Email</th><th>Permission</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} style={{ opacity: u.is_active === false ? 0.5 : 1 }}>
                      <td style={{ fontWeight: 600 }}>{u.full_name || '—'}</td>
                      <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{u.email || '—'}</td>
                      <td>
                        <select className="form-select" style={{ width: 110 }} value={u.permission_level || 'staff'}
                          onChange={e => handleRoleChange(u.id, e.target.value)}
                          disabled={u.id === myProfile?.id}>
                          {permLevels.map(l => <option key={l} value={l}>{l}</option>)}
                        </select>
                      </td>
                      <td><span className={`badge ${u.is_active !== false ? 'badge-green' : 'badge-grey'}`}>{u.is_active !== false ? 'Active' : 'Inactive'}</span></td>
                      <td>
                        <button className="btn btn-secondary btn-sm"
                          style={{ color: u.is_active !== false ? 'var(--red)' : 'var(--green-mid)' }}
                          onClick={() => handleDeactivate(u.id, u.is_active !== false)}
                          disabled={u.id === myProfile?.id}>
                          {u.is_active !== false ? 'Deactivate' : 'Reactivate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )
          )}

          {userTab === 'manual' && (
            <div>
              <div className="alert alert-amber" style={{ marginBottom: 14, fontSize: '0.78rem' }}>
                📋 Manual workers cannot log into the system. A manager or admin must record their timesheets, tasks and activities on their behalf by selecting their name from dropdowns throughout the app.
              </div>
              {manualWorkers.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">👷</div>
                  <h3>No manual workers yet</h3>
                  <p>Click "+ Manual Worker" above to add staff who don't have system access.</p>
                </div>
              ) : (
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Name</th><th>Role</th><th>Department</th><th>Notes</th><th>Added</th><th>Actions</th></tr></thead>
                    <tbody>
                      {manualWorkers.map(w => (
                        <tr key={w.id} style={{ opacity: w.is_active === false ? 0.5 : 1 }}>
                          <td style={{ fontWeight: 600 }}>{w.full_name}</td>
                          <td style={{ fontSize: '0.78rem' }}>{w.role || '—'}</td>
                          <td style={{ fontSize: '0.78rem' }}>{w.department || '—'}</td>
                          <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{w.notes || '—'}</td>
                          <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{new Date(w.created_at).toLocaleDateString('en-UG')}</td>
                          <td>
                            <button className="btn btn-secondary btn-sm" style={{ color: 'var(--red)' }}
                              onClick={async () => {
                                await supabase.from('manual_workers').update({ is_active: !w.is_active }).eq('id', w.id)
                                loadManualWorkers()
                              }}>
                              {w.is_active !== false ? 'Deactivate' : 'Reactivate'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 20, padding: 14, background: 'var(--cream)', borderRadius: 8, fontSize: '0.78rem', color: 'var(--text-muted)' }}>
            <strong>Permission levels:</strong> Admin — full access including finance & period close. Manager — all operations except admin settings. Staff — daily operations only. View Only — read access only.
          </div>
        </div>
      )}

      {/* PASSWORD RESETS TAB */}
      {tab === 'pw' && (
        <div className="card">
          <div className="card-title">Password Reset Requests</div>
          <div className="card-subtitle">When a user requests a password reset, it appears here. Approve to send them a secure reset link by email.</div>
          {pwResetRequests.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">🔑</div><h3>No pending requests</h3><p>Password reset requests from staff will appear here for your approval.</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Name</th><th>Email</th><th>Requested</th><th>Reason</th><th>Actions</th></tr></thead>
                <tbody>
                  {pwResetRequests.map(r => (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 600 }}>{r.profiles?.full_name || '—'}</td>
                      <td style={{ fontSize: '0.78rem' }}>{r.profiles?.email || '—'}</td>
                      <td style={{ fontSize: '0.78rem' }}>{new Date(r.created_at).toLocaleDateString('en-UG')}</td>
                      <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{r.reason || '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-primary btn-sm" onClick={() => handleApproveReset(r)} disabled={saving}>✓ Approve & Send Link</button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDenyReset(r)}>✗ Deny</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* INVITE MODAL */}
      {inviteModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setInviteModal(false)}>
          <div className="modal">
            <div className="modal-title">Invite User with Email</div>
            <div className="modal-subtitle">They'll receive an email with a secure link to set their password. Passwords must be strong (8+ chars, uppercase, lowercase, number, symbol).</div>
            <div className="form-group"><label className="form-label">Full Name *</label><input className="form-input" value={inviteForm.full_name} onChange={e => setInviteForm(f => ({ ...f, full_name: e.target.value }))} placeholder="e.g. John Mugisha" /></div>
            <div className="form-group"><label className="form-label">Email Address *</label><input className="form-input" type="email" value={inviteForm.email} onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))} placeholder="name@anyemail.com" /></div>
            <div className="form-group">
              <label className="form-label">Permission Level</label>
              <select className="form-select" value={inviteForm.permission_level} onChange={e => setInviteForm(f => ({ ...f, permission_level: e.target.value }))}>
                {permLevels.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Dashboard Role</label>
              <select className="form-select" value={inviteForm.user_role} onChange={e => setInviteForm(f => ({ ...f, user_role: e.target.value }))}>
                <option value="staff">Staff — Farm log, harvest, timesheets only</option>
                <option value="ops_admin">Operations Admin — Full ops + read-only finance</option>
                <option value="finance_admin">Finance Admin — Full access including journals</option>
              </select>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                Controls which dashboard they see and which menus are visible.
              </div>
            </div>
            <div style={{ background: 'var(--green-pale)', borderRadius: 8, padding: 12, fontSize: '0.78rem', marginBottom: 16 }}>
              🔒 <strong>Password requirements:</strong> Minimum 8 characters · At least one uppercase letter · One lowercase · One number · One special character (!@#$%^&*)
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setInviteModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleInvite} disabled={inviting}>{inviting ? 'Sending...' : '📧 Send Invite'}</button>
            </div>
          </div>
        </div>
      )}

      {/* MANUAL WORKER MODAL */}
      {manualModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setManualModal(false)}>
          <div className="modal">
            <div className="modal-title">Add Manual Worker</div>
            <div className="modal-subtitle">For line workers, temporary staff, or anyone without a phone or email. Their data will be entered by a supervisor on their behalf. They do not get system access.</div>
            <div className="alert alert-amber" style={{ marginBottom: 14, fontSize: '0.78rem' }}>
              ⚠️ Manual workers cannot log into the system. All their entries (timesheets, tasks, etc.) must be recorded by a manager or admin who logs in and selects this person's name.
            </div>
            <div className="form-group"><label className="form-label">Full Name *</label><input className="form-input" value={manualForm.full_name} onChange={e => setManualForm(f => ({ ...f, full_name: e.target.value }))} placeholder="e.g. Nakato Sarah" /></div>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Role / Job Title</label><input className="form-input" value={manualForm.role} onChange={e => setManualForm(f => ({ ...f, role: e.target.value }))} placeholder="e.g. Harvest Worker" /></div>
              <div className="form-group"><label className="form-label">Department</label>
                <select className="form-select" value={manualForm.department} onChange={e => setManualForm(f => ({ ...f, department: e.target.value }))}>
                  <option value="">Select...</option>
                  {['Farm Operations', 'Harvest', 'Logistics', 'Packaging', 'General'].map(d => <option key={d}>{d}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group"><label className="form-label">Notes (optional)</label><textarea className="form-textarea" style={{ minHeight: 60 }} value={manualForm.notes} onChange={e => setManualForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. Temporary harvest season worker. Contract ends 31 March." /></div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setManualModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddManual} disabled={saving}>{saving ? 'Saving...' : '+ Add Worker'}</button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
